// Global auth interceptor for admin panel - automatically logout on 401/403 or token expiration
import axios from 'axios';

let isLoggedOut = false;

const logout = () => {
  if (isLoggedOut) return; // Prevent multiple logouts
  isLoggedOut = true;
  
  console.log('🚪 Admin Auto-logout: Token expired or unauthorized');
  localStorage.removeItem('adminAccessToken');
  localStorage.removeItem('adminRefreshToken');
  localStorage.removeItem('adminUser');
  sessionStorage.clear();
  
  window.location.href = '/login';
};

// Check if token is expired
const isTokenExpired = (token: string): boolean => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000; // Convert to milliseconds
    const isExpired = Date.now() >= exp;
    
    if (isExpired) {
      console.log('⏰ Admin token expired at:', new Date(exp).toLocaleString());
    }
    
    return isExpired;
  } catch (error) {
    console.error('❌ Error parsing admin token:', error);
    return true; // If can't parse, consider expired
  }
};

// Check token expiration every 10 seconds
const checkInterval = setInterval(() => {
  const token = localStorage.getItem('adminAccessToken');
  if (token && isTokenExpired(token)) {
    clearInterval(checkInterval);
    logout();
  }
}, 10000); // Check every 10 seconds

// Check token immediately on load
const token = localStorage.getItem('adminAccessToken');
if (token && isTokenExpired(token)) {
  logout();
}

// Override fetch to intercept all API calls
const originalFetch = window.fetch;
window.fetch = async (...args) => {
  const response = await originalFetch(...args);
  
  // Check for auth errors
  if (response.status === 401 || response.status === 403) {
    console.log('🚫 Admin API returned', response.status, '- logging out');
    logout();
  }
  
  return response;
};

// Intercept axios requests
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    // Check for auth errors
    if (error.response && (error.response.status === 401 || error.response.status === 403)) {
      console.log('🚫 Admin Axios API returned', error.response.status, '- logging out');
      logout();
    }
    return Promise.reject(error);
  }
);

// Also check token before each axios request
axios.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('adminAccessToken');
    if (token && isTokenExpired(token)) {
      console.log('⏰ Admin token expired before request - logging out');
      logout();
      return Promise.reject(new Error('Token expired'));
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

export { logout, isTokenExpired };
